import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Grid,
  Button,
  TextField,
  Box,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import axios from "axios";
import PropTypes from "prop-types";


const LeaveActionDialog = ({
  open,
  onClose,
  leaveId,
  onSuccess,
  heading,
  headerBackgroundColors,
}) => {
  const [leaveData, setLeaveData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [showDeclineReason, setShowDeclineReason] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (leaveId && open) {
      setLoading(true);

      axios
        .get(`${process.env.REACT_APP_API_URL}/leave-request/fetch/${leaveId}`, {
          withCredentials: true,
        })
        .then((response) => {
          if (response.data.status === 200) {
            setLeaveData(response.data.data);
          }
        })
        .catch((error) => {
          console.error("Failed to fetch leave data:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [leaveId, open]);

  const handleApproveClick = () => {
    setShowDeclineReason(false);
    setActionType("approve");
    setShowConfirmDialog(true);
  };

  const handleDeclineClick = () => {
    if (!showDeclineReason) return setShowDeclineReason(true);
    if (!declineReason.trim()) {
      alert("Please enter a reason before declining.");
      return;
    }
    setActionType("decline");
    setShowConfirmDialog(true);
  };

  const getDialogBackground = () => {
  if (actionType === "approve") {
    return "linear-gradient(45deg, #2e7d32, #191919)"; // Success green
  } else if (actionType === "decline") {
    return "linear-gradient(45deg, #800000, #191919)"; // Maroon red
  }
  return "linear-gradient(45deg, #f48c06, #191919)"; // Default orange
};

const getHoverBackground = () => {
  if (actionType === "approve") {
    return "linear-gradient(45deg, #1e5722, #101010)"; // Darker green
  } else if (actionType === "decline") {
    return "linear-gradient(45deg, #5a0000, #101010)"; // Darker maroon
  }
  return "linear-gradient(45deg, #c07604, #101010)"; // Hover for orange default
};

  const handleConfirm = async () => {
    setShowConfirmDialog(false);
    let res;

    try {
      if (actionType === "approve") {
        res = await axios.put(
          `${process.env.REACT_APP_API_URL}/leave-request/approve/${leaveId}`,
          {},
          { withCredentials: true }
        );
        console.log(res.data.message);
      } else {
        res = await axios.put(
          `${process.env.REACT_APP_API_URL}/leave-request/decline/${leaveId}`,
          { reason: declineReason },
          { withCredentials: true }
        );
        console.log(res.data.message);
      }

      if (res.data?.message) {
        setSnackbar({
          open: true,
          message: res.data.message,
          severity: "success",
        });

        if (typeof onSuccess === "function") onSuccess();

        setTimeout(() => {
          onClose();
        }, 1000);
      }
    } catch (error) {
      console.error("Error updating leave status:", error);
      setSnackbar({
        open: true,
        message: "Something went wrong. Please try again.",
        severity: "error",
      });
    }
  };

  const [rotate, setRotate] = React.useState(0);
  const handleMouseEnter = () => {
    setRotate(360);
  };

  const handleMouseLeave = () => {
    setRotate(180);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiPaper-root": {
            borderRadius: 2,
            boxShadow: 6,
            position: "relative",
            overflow: "visible",
          },
        }}
      >
        <IconButton
          onClick={onClose}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{
            position: "absolute",
            top: -18,
            right: -16,
            backgroundColor: "#fff",
            color: "#ff0a54",
            boxShadow: 1,
            zIndex: 1500,
            p: 1.5,
            "&:hover": {
              backgroundColor: "#ff0a54",
              color: "#fff",
            },
            transition: "transform 0.5s ease",
            transform: `rotate(${rotate}deg)`,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle
          sx={{
            background: `linear-gradient(45deg, #f48c06, #191919)`,
            color: "white !important",
            fontWeight: "bold",
            fontSize: "18px",
            textAlign: "center",
            position: "relative",
            zIndex: 1400,
            py: 2,
          }}
        >
          {heading ? heading : "Leave Request Details"}
        </DialogTitle>
        <DialogContent>
          {loading || !leaveData ? (
            <Typography>Loading...</Typography>
          ) : (
            <Box>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item size={{ xs: 6, md: 4 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      User Name
                    </Typography>
                    <Typography variant="body1">{leaveData.user_name}</Typography>
                  </Grid>

                  <Grid item size={{ xs: 6, md: 4 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      District
                    </Typography>
                    <Typography variant="body1">{leaveData.district_name}</Typography>
                  </Grid>

                  <Grid item size={{ xs: 6, md: 4 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Leave Type
                    </Typography>
                    <Typography variant="body1">{leaveData.leave_type}</Typography>
                  </Grid>

                  <Grid item size={{ xs: 6, md: 4 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Start Date
                    </Typography>
                    <Typography variant="body1">
                      {new Date(leaveData.start_date).toLocaleDateString("en-IN")}
                    </Typography>
                  </Grid>

                  <Grid item size={{ xs: 6, md: 4 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      End Date
                    </Typography>
                    <Typography variant="body1">
                      {new Date(leaveData.end_date).toLocaleDateString("en-IN")}
                    </Typography>
                  </Grid>

                  <Grid item size={{ xs: 6, md: 4 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Reason
                    </Typography>
                    <Typography variant="body1">{leaveData.reason}</Typography>
                  </Grid>

                  {leaveData.documents && (() => {
                    try {
                      const docs = JSON.parse(leaveData.documents);
                      if (Array.isArray(docs) && docs.length > 0) {
                        return (
                          <Grid item size={{ xs: 12 }} xs={12} mt={2}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Uploaded Documents
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={1} mt={1}>
                              {docs.map((url, idx) => (
                                <Typography
                                  key={idx}
                                  component="a"
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    color: "#1976d2",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    "&:hover": { color: "#115293" },
                                  }}
                                >
                                  📄 Document {idx + 1}
                                </Typography>
                              ))}
                            </Box>
                          </Grid>
                        );
                      }
                    } catch (e) {
                      // ignore
                    }
                    return null;
                  })()}
                </Grid>
              </Box>
              {showDeclineReason && (
                <Box mt={2}>
                  <TextField
                    label="Reason for Declining"
                    fullWidth
                    multiline
                    rows={4}
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    inputProps={{ maxLength: 500 }}
                    helperText={`${declineReason.length}/500`}
                  />
                </Box>
              )}

              <Box display="flex" justifyContent="center" gap={2} mt={4}>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#70e000",
                    color: "#fff",
                    transition: "transform 0.3s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#008000",
                      color: "#fff",
                      fontWeight: "bold",
                      transform: "scale(1.05)",
                      boxShadow: "0 4px 10px rgba(0, 0, 0, 0.4)",
                    },
                  }}
                  // startIcon={<CheckCircleIcon />}
                  onClick={handleApproveClick}
                >
                  Approve
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#d32f2f",
                    color: "#fff",
                    transition: "transform 0.3s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#9a0007",
                      color: "#fff",
                      fontWeight: "bold",
                      transform: "scale(1.05)",
                      boxShadow: "0 4px 10px rgba(0, 0, 0, 0.4)",
                    },
                  }}
                  // startIcon={<CancelIcon />}
                  onClick={handleDeclineClick}
                >
                  Decline
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiPaper-root": {
            borderRadius: 2,
            boxShadow: 6,
            position: "relative",
            overflow: "visible",
          },
        }}
      >
        <IconButton
          onClick={() => setShowConfirmDialog(false)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{
            position: "absolute",
            top: -18,
            right: -16,
            backgroundColor: "#fff",
            color: "#ff0a54",
            boxShadow: 1,
            zIndex: 1500,
            p: 1.5,
            "&:hover": {
              backgroundColor: "#ff0a54",
              color: "#fff",
            },
            transition: "transform 0.5s ease",
            transform: `rotate(${rotate}deg)`,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle
          sx={{
            background: getDialogBackground(),
            color: "white !important",
            fontWeight: "bold",
            fontSize: "18px",
            textAlign: "center",
            position: "relative",
            zIndex: 1400,
            py: 2,
          }}
        >
          Confirm {actionType === "approve" ? "Approval" : "Decline"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#000" }}>
            Are you sure you want to {actionType === "approve" ? "approve" : "decline"} this leave
            request?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleConfirm}
            variant="contained"
            size="small"
            sx={{
              background: getDialogBackground(),
              color: "#fff",
              fontWeight: "bold",
              transition: "transform 0.3s ease-in-out",
              "&:hover": {
                background: getHoverBackground(),
                color: "#fff",
                fontWeight: "bold",
                transform: "scale(1.05)",
                boxShadow: "0 4px 10px rgba(0, 0, 0, 0.4)",
              },
            }}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

LeaveActionDialog.propTypes = {
  open: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  leaveId: PropTypes.string.isRequired,
  onSuccess: PropTypes.string.isRequired,
  heading: PropTypes.string.isRequired,
  headerBackgroundColors: PropTypes.string.isRequired,
};

export default LeaveActionDialog;
